import { AttachMoney, Edit } from '@mui/icons-material';
import { FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, IconButton, MenuItem, Select, Switch, TextField, Typography } from '@mui/material';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaterialLineItemDto, MaterialPriceDto, OrderDto, QuoteDto, RateToggle, RegionDto, SiteHaulingLineItemDto } from '../../../../../dtos';
import {
    useArchiveLineItemByIdMutation,
    useArchiveMaterialLineItemDeleteFromDatabaseByIdMutation,
    useGetLineItemHasBeenOrderedQuery,
    useLazyGetMaterialPriceQuery,
    useLazyGetSiteHaulingLineItemQuery,
    usePatchLineItemByIdMutation,
} from '../../../../../store/generated/generatedApi';
import { emptyGuid, formatCurrency, pricingTypes } from '../../../../../util';
import { EquipmentTypeSelect, MaterialSelect, SiteSelect, UnitOfMeasureSelect, PoAndJobNumberInput, AddressForm } from '../../../../CommonInputs';
import { SlimFormHeader } from '../../../../Core/SlimFormHeader';
import {
    FormNumberInput,
    FormSection,
    IFormProps,
    LoadingIndicator,
    UserPermissionContext,
    userHasPermissions,
    FormInput
} from '../../../../CoreLib/library';
import { useMaterialLineItemForm } from './useMaterialLineItemForm';
import { AddressLookup } from '../../../../CommonInputs/AddressLookup';
import { IEntityAutocomplete } from '../../../../CommonInputs/IEntityAutocomplete';
import { RateInputForm } from '../RateInputForm';
import { BilledToType, BilledToTypeToText } from '../../../../../dtos/enums/BilledToType';
import { MaterialBilledToSelect } from '../../../../CommonInputs/MaterialBilledToSelect';

export interface IMaterialLineItemFormProps extends IFormProps<MaterialLineItemDto> {
    quote?: QuoteDto;
    order?: OrderDto;
}

export const MaterialLineItemForm: FC<IMaterialLineItemFormProps> = (props) => {
    const { isLoading, quote, order } = props;
    const { permissions } = useContext(UserPermissionContext);
    const navigate = useNavigate();
    const {
        isFormDirty,
        handleSave,
        handleCancel,
        fieldErrors,
        handleEquipmentTypeChange,
        handleHaulingDriverRateChange,
        handleHaulingHaulingRateChange,
        handleHaulingBrokerRateChange,
        handleIsBilledToChange,
        handleBilledToChange,
        handleIsMaterialOnlyChange,
        handleMaterialChange,
        handleMaterialPriceChange,
        handlePriceCodeChange,
        handleQuantityChange,
        handleQuoteChange,
        handleRateChange,
        handleCostChange,
        handleSiteChange,
        handleUnitOfMeasureChange,
        handleLineItemMemoChange,
        handleDriverMemoChange,
        handleIsCustomDriverMemoChange,
        handleCustomerJobNumberChange,
        handlePoNumberChange,
        handleHaulingMinimumFlatRateChange,
        handleHaulingDriverMinimumFlatRateChange,
        handleHaulingBrokerMinimumFlatRateChange,
        handleHaulingZoneChange,
        setCurrentSiteHaulingLineItem,
        setFormIsCustomDriverMemo,
        handleRateToggleChange,
        handleIsCustomPoNumberChange,
        handleIsCustomerJobNumberCustomChange,
        handleFormAddressChange,
        handleFormRegionChange,
        handleIsMaterialTaxableChange,
        currentSiteHaulingLineItem,
        formQuote,
        formSite,
        formEquipmentType,
        formRate,
        formQuantity,
        formIsBilledTo,
        formBilledTo,
        formIsMaterialOnly,
        formUnitOfMeasure,
        formIsActive,
        formMaterial,
        formMaterialPrice,
        formPriceCode,
        formCost,
        formTotal,
        formNewHaulingLineItemRate,
        formNewHaulingLineItemDriverRate,
        formNewHaulingLineItemBrokerRate,
        formNewHaulingLineItemMinimumFlatRate,
        formNewHaulingLineItemDriverMinimumFlatRate,
        formNewHaulingLineItemBrokerMinimumFlatRate,
        formNewHaulingLineItemZone,
        formLineItemMemo,
        formDriverMemo,
        formCustomerJobNumber,
        formPoNumber,
        formRateToggle,
        formIsCustomPoNumber,
        formIsCustomerJobNumberCustom,
        formIsCustomDriverMemo,
        formQuotePoNumber,
        formQuoteCustomerJobNumber,
        formSiteName,
        formAddressLine1,
        formAddressLine2,
        formCity,
        formState,
        formZipCode,
        formCountry,
        formDirections,
        formRegion,
        regions,
        validateField,
        isFobQuote,
        initValues,
        getCurrentRate,
        isNewMaterialLineItem,
        formIsMaterialTaxable
    } = useMaterialLineItemForm(props, quote);

    const [getMaterialPrices, { data: materialPrices, isLoading: materialPricesLoading }] = useLazyGetMaterialPriceQuery();
    const [getSiteHaulingLineItems, { data: siteHaulingLineItems, isFetching: siteHaulingLineItemsLoading }] = useLazyGetSiteHaulingLineItemQuery();
    const { data: canDeleteData, error: canDeleteError, isLoading: canDeleteLoading } = useGetLineItemHasBeenOrderedQuery({ id: props.initValues?.id ?? '' });

    const [canDelete, setCanDelete] = useState(true);
    useEffect(() => {
        if (canDeleteError) {
            if ((canDeleteError as any).status === 404) {
                setCanDelete(true);
            }
        }
        if (canDeleteData && canDeleteData === true) {
            setCanDelete(false);
        }
    }, [canDeleteData, canDeleteError]);

    const currentMaterialLineItemId = useMemo(() => {
        return props.initValues?.id;
    }, [props.initValues?.id]);

    useEffect(() => {
        if (isFobQuote) {
            setFormIsCustomDriverMemo(true);
        }
    }, [isFobQuote, setFormIsCustomDriverMemo]);

    useEffect(() => {
        if (quote && !formQuote) {
            handleQuoteChange(quote);
        }
    }, [formQuote, handleQuoteChange, quote]);

    useEffect(() => {
        if (formMaterial) {
            const effectiveOn = quote?.effectiveOn ? new Date(quote.effectiveOn) : undefined;
            getMaterialPrices({
                effectiveOn: effectiveOn?.toISOString(),
                materialId: formMaterial?.id,
                searchText: '',
                sorting: ['MATERIAL_CODE+'],
                page: 0,
                pageSize: 100000,
                includeInactive: false,
            });
        }
    }, [getMaterialPrices, formMaterial, quote?.effectiveOn]);

    useEffect(() => {
        if (formSite && formEquipmentType && formQuote) {
            if (formUnitOfMeasure && formSite?.id) {
                getSiteHaulingLineItems({
                    quoteId: formQuote?.id ?? '',
                    equipmentTypeId: formEquipmentType?.id ?? '',
                    siteId: formSite?.id ?? '',
                    unitOfMeasure: formUnitOfMeasure ?? '',
                    searchText: '',
                    sorting: ['TYPE+'],
                    page: 0,
                    pageSize: 100000,
                    includeInactive: true,
                }).then((result: any) => {
                    if (currentMaterialLineItemId !== emptyGuid) {
                        if (props.initValues?.associatedLineItemId) {
                            const lineItem = result.data.pageResults.find((x) => x.id === props.initValues?.associatedLineItemId);
                            setCurrentSiteHaulingLineItem(lineItem);
                            handleRateToggleChange(lineItem?.rateToggle);
                        } else {
                            setCurrentSiteHaulingLineItem(result.data.pageResults[0]);
                            handleRateToggleChange(result.data.pageResults[0].rateToggle);
                        }
                    } else {
                        setCurrentSiteHaulingLineItem(result.data.pageResults[0]);
                        handleRateToggleChange(result.data.pageResults[0].rateToggle);
                    }
                });
            }
        }
    }, [formEquipmentType, formSite, formQuote, formSite?.id, formUnitOfMeasure, getSiteHaulingLineItems, currentMaterialLineItemId, props.initValues?.associatedLineItemId, setCurrentSiteHaulingLineItem, handleRateToggleChange]);

    const canEditCost = useMemo(() => {
        const isUserAllowedToEditCost = userHasPermissions(['edit:setCustomCost'], permissions, 'and');
        if (initValues?.priceCode === 'Custom') {
            return isUserAllowedToEditCost;
        } else {
            return true;
        }
    }, [initValues?.priceCode, permissions]);

    const handleHaulingLineItemEdit = useCallback(() => {
        if (currentSiteHaulingLineItem) {
            navigate(`/quote/${quote?.id}/lineItem/siteHauling/${currentSiteHaulingLineItem.id}`);
        }
    }, [currentSiteHaulingLineItem, navigate, quote?.id]);

    const newHaulingFormInputs = useCallback(() => {
        return (
            <RateInputForm
                haulingRate={formNewHaulingLineItemRate}
                setHaulingRate={handleHaulingHaulingRateChange}
                haulingRateFieldName='newHaulingLineItemRate'
                minimumFlatRate={formNewHaulingLineItemMinimumFlatRate}
                setMinimumFlatRate={handleHaulingMinimumFlatRateChange}
                minimumFlatRateFieldName='newHaulingLineItemMinimumFlatRate'
                driverRate={formNewHaulingLineItemDriverRate}
                setDriverRate={handleHaulingDriverRateChange}
                driverRateFieldName='newHaulingLineItemDriverRate'
                driverFlatRate={formNewHaulingLineItemDriverMinimumFlatRate}
                setDriverFlatRate={handleHaulingDriverMinimumFlatRateChange}
                driverFlatRateFieldName='newHaulingLineItemDriverMinimumFlatRate'
                brokerRate={formNewHaulingLineItemBrokerRate}
                setBrokerRate={handleHaulingBrokerRateChange}
                brokerRateFieldName='newHaulingLineItemBrokerRate'
                brokerFlatRate={formNewHaulingLineItemBrokerMinimumFlatRate}
                setBrokerFlatRate={handleHaulingBrokerMinimumFlatRateChange}
                brokerFlatRateFieldName='newHaulingLineItemBrokerMinimumFlatRate'
                zone={formNewHaulingLineItemZone}
                setZone={handleHaulingZoneChange}
                rateToggle={formRateToggle}
                setRateToggle={handleRateToggleChange}
                fieldErrors={fieldErrors}
                includeZone={true}
            />
        );
    }, [formNewHaulingLineItemRate, handleHaulingHaulingRateChange, formNewHaulingLineItemMinimumFlatRate, handleHaulingMinimumFlatRateChange, formNewHaulingLineItemDriverRate, handleHaulingDriverRateChange, formNewHaulingLineItemDriverMinimumFlatRate, handleHaulingDriverMinimumFlatRateChange, formNewHaulingLineItemBrokerRate, handleHaulingBrokerRateChange, formNewHaulingLineItemBrokerMinimumFlatRate, handleHaulingBrokerMinimumFlatRateChange, formNewHaulingLineItemZone, handleHaulingZoneChange, formRateToggle, handleRateToggleChange, fieldErrors]);

    const haulingCardDetails = useCallback(() => {
        if (siteHaulingLineItemsLoading || !siteHaulingLineItems) {
            return <LoadingIndicator />;
        }
        if (currentSiteHaulingLineItem) {
            const haulingItem = currentSiteHaulingLineItem;
            return (
                <Grid item>
                    <Grid item>Site: {haulingItem.site?.code}</Grid>
                    <Grid item>Unit of Measure: {haulingItem.unitOfMeasure}</Grid>
                    <Grid item>Equipment Type: {haulingItem.equipmentType?.type}</Grid>
                    {haulingItem.rateToggle !== RateToggle.Flat && <Grid item>Hauling Rate: {formatCurrency(haulingItem.haulingRate)}</Grid>}
                    {haulingItem.rateToggle !== RateToggle.Standard && <Grid item>Minimum Flat Rate: {formatCurrency(haulingItem.haulingFlatRate)}</Grid>}
                    {haulingItem.rateToggle !== RateToggle.Flat && <Grid item>Driver Rate: {formatCurrency(haulingItem.driverRate)}</Grid>}
                    {haulingItem.rateToggle !== RateToggle.Standard && <Grid item>Driver Minimum Flat Rate: {formatCurrency(haulingItem.driverFlatRate)}</Grid>}
                    {haulingItem.rateToggle !== RateToggle.Flat && <Grid item>Broker Rate: {formatCurrency(haulingItem.brokerRate)}</Grid>}
                    {haulingItem.rateToggle !== RateToggle.Standard && <Grid item>Broker Minimum Flat Rate: {formatCurrency(haulingItem.brokerFlatRate)}</Grid>}
                    <Grid item>Zone: {haulingItem.zone}</Grid>
                </Grid>
            );
        } else {
            return newHaulingFormInputs();
        }
    }, [currentSiteHaulingLineItem, newHaulingFormInputs, siteHaulingLineItems, siteHaulingLineItemsLoading]);

    const getHaulingCardTitle = useCallback(() => {
        if (currentSiteHaulingLineItem) {
            return `Hauling to Site ${currentSiteHaulingLineItem.site?.code}`;
        } else {
            return 'New Haul Rate Line Item';
        }
    }, [currentSiteHaulingLineItem]);

    const haulingCard = useCallback(() => {
        if (formSite && formEquipmentType && formUnitOfMeasure) {
            if (siteHaulingLineItemsLoading || !siteHaulingLineItems) {
                return <LoadingIndicator />;
            }
            return (
                <FormSection>
                    <Grid item container justifyContent='space-between'>
                        <Grid item>
                            <Typography variant='h2'>{getHaulingCardTitle()}</Typography>
                        </Grid>
                        <Grid item>
                            {currentSiteHaulingLineItem ? (
                                <IconButton onClick={handleHaulingLineItemEdit} sx={{ padding: '0px' }}>
                                    <Edit />
                                </IconButton>
                            ) : (
                                <></>
                            )}
                        </Grid>
                    </Grid>
                    <Grid item>
                        <FormControl fullWidth>
                            <IEntityAutocomplete
                                options={siteHaulingLineItems?.pageResults}
                                onChange={(e, value) => {
                                    if (value?.id) {
                                        setCurrentSiteHaulingLineItem(value);
                                    } else {
                                        setCurrentSiteHaulingLineItem(undefined);
                                    }
                                }}
                                value={currentSiteHaulingLineItem ?? { id: '', isActive: true }}
                                getOptionLabel={(option: SiteHaulingLineItemDto) => {
                                    if (!option.id) {
                                        return 'Create New';
                                    }
                                    switch (option.rateToggle) {
                                        case RateToggle.Standard:
                                            return `Standard: ${formatCurrency(option.haulingRate)}`;
                                        case RateToggle.Flat:
                                            return `Flat: ${formatCurrency(option.haulingFlatRate)}`;
                                        default:
                                            return `Standard: ${formatCurrency(option.haulingRate)} | Flat: ${formatCurrency(option.haulingFlatRate)}`;
                                    }
                                }}
                                defaultOptions={[
                                    {
                                        id: '',
                                        isActive: true
                                    }
                                ]}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item>{haulingCardDetails()}</Grid>
                </FormSection>
            );
        }
    }, [currentSiteHaulingLineItem, formEquipmentType, formSite, formUnitOfMeasure, getHaulingCardTitle, handleHaulingLineItemEdit, haulingCardDetails, setCurrentSiteHaulingLineItem, siteHaulingLineItems, siteHaulingLineItemsLoading]);

    const filteredPricingTypes = useMemo(() => {
        var filteredList = [...pricingTypes];
        const shouldRemovePriceD = !formMaterialPrice?.currentPriceD;
        if (shouldRemovePriceD) {
            filteredList = filteredList.filter((pt) => pt !== 'D');
        }
        const shouldRemovePriceE = !formMaterialPrice?.currentPriceE;
        if (shouldRemovePriceE) {
            filteredList = filteredList.filter((pt) => pt !== 'E');
        }
        return filteredList;
    }, [formMaterialPrice]);

    const getMaterialNetCost = useCallback((materialPrice: MaterialPriceDto) => {
        return `(Net: ${formatCurrency((materialPrice.currentPriceC ?? 0) - (materialPrice.currentCost ?? 0))})`;
    }, []);

    if (isLoading || canDeleteLoading) {
        return <LoadingIndicator />;
    }

    return (
        <Grid container direction='column'>
            <SlimFormHeader
                objectName='Haul Rate Line Item'
                permissionsTypeName={'lineItem'}
                icon={<AttachMoney />}
                title={formSite ? `${formMaterial?.code} from site ${formSite.code}` : 'New Material Line Item'}
                titleMaxWidth="60vw"
                breadcrumbs={
                    order
                        ? [
                            { label: 'Home', navLink: '/' },
                            { label: 'Jobs', navLink: '/jobs' },
                            { label: `Job #${quote?.quoteNumber}`, navLink: `/job/${order?.jobId}` },
                            { label: `Order #${order?.orderNumber}`, navLink: `/job/${order?.jobId}/order/${order?.id}` },
                        ]
                        : [
                            { label: 'Home', navLink: '/' },
                            { label: 'Quotes', navLink: '/quotes' },
                            { label: `${quote?.jobName ? `${quote.jobName} - ` : ''}${quote?.customer?.customerNumber ? `${quote.customer.customerNumber}: ` : ''}${quote?.customer?.name ?? ''} (Quote #${quote?.quoteNumber ?? 'Quote'})`, navLink: `/quote/${quote?.id}` },
                            { label: `Line Items`, navLink: `/quote/${quote?.id}/lineItems` },
                        ]
                }
                isActive={formIsActive}
                id={currentMaterialLineItemId}
                isFormDirty={isFormDirty}
                handleCancel={handleCancel}
                handleSave={handleSave}
                entityNameSingular={'haul rate line item'}
                canSave={true}
                canDelete={canDelete}
                deleteMutation={useArchiveMaterialLineItemDeleteFromDatabaseByIdMutation}
                archiveRestoreProps={{
                    restoreMutation: usePatchLineItemByIdMutation,
                    archiveMutation: useArchiveLineItemByIdMutation,
                    isActive: formIsActive,
                }}
            />
            <Grid item container direction='row' xs={12} spacing={2} px={2}>
                <Grid item xs={8}>
                    <FormSection>
                        <Grid item>
                            <Typography variant='h2'>General</Typography>
                        </Grid>
                        <Grid item container direction='row' spacing={3} alignItems='center'>
                            <Grid item xs={12} sm={6} md={6} xl={6}>
                                <EquipmentTypeSelect
                                    selectedEquipmentTypeId={formEquipmentType?.id ?? ''}
                                    handleSelectedEquipmentTypeChange={handleEquipmentTypeChange}
                                    required
                                    errorMessage={fieldErrors.get('equipmentTypeId')}
                                    defaultToDTEquipmentType={isNewMaterialLineItem}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} xl={6}>
                                <MaterialSelect
                                    selectedMaterialId={formMaterial?.id ?? ''}
                                    handleSelectedMaterialChange={handleMaterialChange}
                                    required
                                    errorMessage={fieldErrors.get('materialId')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} xl={8}>
                                <FormControl
                                    error={!!fieldErrors.get('materialPriceId')}
                                    fullWidth
                                    required={formIsBilledTo === BilledToType.TriAx}
                                    disabled={!formMaterial || formIsBilledTo === BilledToType.Customer || formIsBilledTo === BilledToType.Custom}>
                                    <FormLabel>Site + Pricing</FormLabel>
                                    <IEntityAutocomplete
                                        options={materialPrices?.pageResults}
                                        onChange={(e, value) => {
                                            handleMaterialPriceChange(value ?? null);
                                        }}
                                        value={formMaterialPrice}
                                        getOptionLabel={(option: MaterialPriceDto) =>
                                            `Site ${option.site?.code} - ${formatCurrency(option.currentCost)} ${getMaterialNetCost(option)}`
                                        }
                                        isLoading={materialPricesLoading}
                                        disabled={!formMaterial || formIsBilledTo === BilledToType.Customer || formIsBilledTo === BilledToType.Custom}
                                        error={!!fieldErrors.get('materialPriceId')}
                                    />
                                    <FormHelperText>{fieldErrors.get('materialPriceId')}</FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3} xl={2}>
                                <MaterialBilledToSelect
                                    selectedBilledTo={formIsBilledTo}
                                    handleSelectedBilledToChange={handleIsBilledToChange}
                                    required
                                    errorMessage={fieldErrors.get('isBilledTo')}
                                    onBlur={() => validateField('isBilledTo')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3} xl={2}>
                                <FormInput
                                    label='Material Bill To'
                                    value={BilledToTypeToText(formIsBilledTo, quote?.customer?.name ?? '', formBilledTo)}
                                    onChange={handleBilledToChange}
                                    required={formIsBilledTo === BilledToType.Custom}
                                    errorText={fieldErrors.get('billedTo')}
                                    onBlur={() => validateField('billedTo')}
                                    disabled={formIsBilledTo !== BilledToType.Custom}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <PoAndJobNumberInput
                                    formQuotePoNumber={formQuotePoNumber}
                                    formIsCustomPoNumber={formIsCustomPoNumber}
                                    handleIsCustomPoNumberChange={handleIsCustomPoNumberChange}
                                    formPoNumber={formPoNumber}
                                    formQuoteCustomerJobNumber={formQuoteCustomerJobNumber}
                                    formIsCustomerJobNumberCustom={formIsCustomerJobNumberCustom}
                                    formCustomerJobNumber={formCustomerJobNumber}
                                    handleIsCustomerJobNumberCustomChange={handleIsCustomerJobNumberCustomChange}
                                    handleCustomerJobNumberChange={handleCustomerJobNumberChange}
                                    handlePoNumberChange={handlePoNumberChange}
                                    fieldErrors={fieldErrors}
                                />
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Typography variant='h2'>Pricing</Typography>
                        </Grid>
                        <Grid item container direction='row' spacing={3}>
                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <FormControl
                                    fullWidth
                                    disabled={!formMaterialPrice || !canEditCost}
                                    required
                                    error={!!fieldErrors.get('priceCode')}>
                                    <FormLabel>Pricing</FormLabel>
                                    <Select value={formPriceCode ?? ''} onChange={handlePriceCodeChange} error={!!fieldErrors.get('priceCode')}>
                                        <MenuItem key='pricing-0' value={''}>
                                            None
                                        </MenuItem>
                                        {filteredPricingTypes.map((value) => (
                                            <MenuItem
                                                key={value}
                                                value={value}
                                                disabled={value === 'Custom' && !userHasPermissions(['edit:setCustomCost'], permissions, 'and')}>
                                                {value}
                                                {value !== 'Custom' ? ` - ${formatCurrency(getCurrentRate(value))}` : ''}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>{fieldErrors.get('priceCode')}</FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <UnitOfMeasureSelect
                                    selectedUnit={formUnitOfMeasure ?? ''}
                                    handleSelectedUnitChange={handleUnitOfMeasureChange}
                                    required
                                    errorMessage={fieldErrors.get('unitOfMeasure')}
                                    onBlur={() => validateField('unitOfMeasure')}
                                    disabled={
                                        formIsBilledTo !== BilledToType.Customer &&
                                        formIsBilledTo !== BilledToType.Custom &&
                                        (!formMaterialPrice || formPriceCode === '')
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <FormNumberInput
                                    value={formCost}
                                    onChange={handleCostChange}
                                    label='Cost'
                                    name='cost'
                                    fullWidth
                                    disabled={formPriceCode !== 'Custom' || !canEditCost}
                                    inputProps={{ min: 0 }}
                                    required
                                    error={!!fieldErrors.get('cost')}
                                    errorText={fieldErrors.get('cost')}
                                    isCurrency
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <FormNumberInput
                                    value={formQuantity ?? ''}
                                    onChange={handleQuantityChange}
                                    label='Quantity'
                                    name='quantity'
                                    errorText={fieldErrors.get('quantity')}
                                    error={!!fieldErrors.get('quantity')}
                                    fullWidth
                                    disabled={
                                        formIsBilledTo !== BilledToType.Customer &&
                                        formIsBilledTo !== BilledToType.Custom &&
                                        (!formMaterialPrice || formPriceCode === '')
                                    }
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <FormNumberInput
                                    value={formRate ?? ''}
                                    onChange={handleRateChange}
                                    label='Rate'
                                    name='rate'
                                    errorText={fieldErrors.get('rate')}
                                    error={!!fieldErrors.get('rate')}
                                    fullWidth
                                    required
                                    disabled={
                                        formIsBilledTo !== BilledToType.Customer &&
                                        formIsBilledTo !== BilledToType.Custom &&
                                        (!formMaterialPrice || formPriceCode === '')
                                    }
                                    isCurrency
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <FormNumberInput
                                    value={formTotal ?? ''}
                                    onChange={() => { }}
                                    label='Total'
                                    name='total'
                                    fullWidth
                                    disabled
                                    errorText={fieldErrors.get('total')}
                                    error={!!fieldErrors.get('total')}
                                    inputProps={{ min: 0 }}
                                    isCurrency
                                />
                            </Grid>
                        </Grid>
                        {!isFobQuote && (
                            <>
                                <Grid item>
                                    <Typography variant='h2'>Haul From</Typography>
                                </Grid>
                                <Grid item container direction='row' spacing={3}>
                                    <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                                        <SiteSelect
                                            selectedSiteId={formSite?.id ?? ''}
                                            handleSelectedSiteChange={handleSiteChange}
                                            required
                                            errorMessage={fieldErrors.get('siteId')}
                                            disabled={formIsBilledTo === BilledToType.TriAx}
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <AddressLookup
                                            handleAddressChange={handleFormAddressChange}
                                            key={0}
                                            disabled={isLoading}
                                        />
                                    </Grid>
                                    <Grid item xs={2}>
                                        <FormControl fullWidth disabled={isLoading}>
                                            <FormLabel>Site Name</FormLabel>
                                            <TextField
                                                value={formSiteName}
                                                name='siteName'
                                                fullWidth
                                                variant='outlined'
                                                disabled
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <FormControl fullWidth>
                                            <FormLabel>Region</FormLabel>
                                            <IEntityAutocomplete
                                                options={regions?.pageResults ?? []}
                                                onChange={(event, value) => handleFormRegionChange(value)}
                                                value={formRegion}
                                                getOptionLabel={(option: RegionDto) => option.name}
                                                disabled={isLoading}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <FormControlLabel
                                            control={<Switch checked={formIsMaterialOnly} onChange={handleIsMaterialOnlyChange} />}
                                            label='Material Only'
                                            labelPlacement='end'
                                        />
                                    </Grid>
                                    <Grid item xs={12} container direction={'row'} spacing={2} wrap='nowrap'>
                                        <AddressForm
                                            handleAddressChange={handleFormAddressChange}
                                            addressLine1={formAddressLine1}
                                            addressLine2={formAddressLine2}
                                            city={formCity}
                                            state={formState}
                                            zipCode={formZipCode}
                                            country={formCountry}
                                            directions={formDirections}
                                            enableLookup={false}
                                            disabled={isLoading}
                                            allOptional
                                        />
                                    </Grid>
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formIsMaterialTaxable ?? true}
                                        onChange={handleIsMaterialTaxableChange}
                                        disabled={!quote?.isTaxable}
                                    />
                                }
                                label='Is Material Taxable'
                                labelPlacement='end'
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                value={formLineItemMemo ?? ''}
                                onChange={handleLineItemMemoChange}
                                label='Line Item Memo'
                                name='lineItemMemo'
                                fullWidth
                                multiline
                                minRows={3}
                            />
                        </Grid>
                        {!isFobQuote && (
                            <Grid item xs={12} container direction='row' spacing={2} alignItems='center'>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formIsCustomDriverMemo}
                                                onChange={handleIsCustomDriverMemoChange}
                                            />}
                                        label='Custom Order Memo'
                                        labelPlacement='end'
                                    />
                                </Grid>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <TextField
                                value={formDriverMemo ?? ''}
                                onChange={handleDriverMemoChange}
                                label='Order Memo'
                                fullWidth
                                multiline
                                minRows={3}
                                disabled={!formIsCustomDriverMemo && currentSiteHaulingLineItem !== undefined}
                                helperText={
                                    !formIsCustomDriverMemo && (
                                        currentSiteHaulingLineItem !== undefined
                                            ? 'Synced from Hauling Line Item. Toggle "Custom Order Memo" to edit.'
                                            : 'This memo will be saved to the new Hauling Line Item'
                                    )
                                }
                            />
                        </Grid>
                    </FormSection>
                </Grid>
                <Grid item xs={4}>
                    <Grid item container direction='column' spacing={3}>
                        <Grid item>
                            <FormSection>
                                <Grid item>
                                    <Typography variant='h2'>{!formSite ? 'No Site Selected' : `${formSite?.code} - ${formSite?.name}`}</Typography>
                                </Grid>
                                {formSite && (
                                    <Grid item container direction='column'>
                                        <Grid item>{formSite?.addressLine1}</Grid>
                                        <Grid item>{formSite?.addressLine2}</Grid>
                                        <Grid item>
                                            {formSite?.city ?? '(City)'}, {formSite?.state ?? '(State)'}, {formSite?.zipCode ?? '(Zip Code)'}
                                        </Grid>
                                        <Grid item>{formSite?.country ?? '(Country)'}</Grid>
                                    </Grid>
                                )}
                            </FormSection>
                        </Grid>
                        {!isFobQuote && !formIsMaterialOnly && <Grid item>{haulingCard()}</Grid>}
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};
